(() => {
  if (customElements.get('fe-order-summary')) return;
  class FeOrderSummary extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.root = this.closest('product-info');
      if (!this.root) return;
      this.config = JSON.parse(this.querySelector('[data-fe-configuration]').textContent);
      this.controller = new AbortController();
      this.previews = new Map();
      this.money = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency });
      this.decimal = new Intl.NumberFormat(this.config.locale).formatToParts(1.1).find(p => p.type === 'decimal')?.value || '.';
      this.observer = new MutationObserver(() => this.schedule());
      this.observe();
      for (const event of ['input', 'change', 'variant:change']) this.root.addEventListener(event, () => this.schedule(), { signal: this.controller.signal });
      this.root.addEventListener('change', event => this.choosePackaging(event.target), { capture: true, signal: this.controller.signal });
      this.root.addEventListener('click', event => {
        const jump = event.target.closest('[data-fe-jump]');
        if (!jump) return;
        const target = this.root.querySelector(`[data-fe-stage="${jump.dataset.feJump}"]`) || document.getElementById(jump.hash.slice(1));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        if (target.matches('[tabindex]')) target.focus({ preventScroll: true });
      }, { signal: this.controller.signal });
      this.reviewWidget = document.getElementById('judgeme_product_reviews');
      if (this.reviewWidget && this.reviewWidget.dataset.productId === this.dataset.product) {
        this.reviewObserver = new MutationObserver(() => this.syncReviews());
        this.reviewObserver.observe(this.reviewWidget, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'data-src'] });
        this.syncReviews();
      }
      this.render();
    }
    observe() {
      this.observer.observe(this.root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'value', 'disabled', 'style'], characterData: true });
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (!this.isConnected) return;
        if (this.renderTimer) return;
        this.renderTimer = window.setTimeout(() => {
          this.renderTimer = null;
          if (this.isConnected) this.render();
        }, 75);
      });
    }
    choosePackaging(input) {
      if (!(input instanceof HTMLInputElement)) return;
      const bundle = this.root.querySelector('.fe-upsell');
      if (!bundle) return;
      const boxes = [...bundle.querySelectorAll('input[data-fe-packaging]')];
      const standard = bundle.querySelector('[data-fe-no-packaging]');
      if (input.matches('[data-fe-packaging]') && input.checked) {
        for (const box of boxes) {
          if (box !== input && box.checked) { box.checked = false; box.dispatchEvent(new Event('change', { bubbles: true })); }
        }
        if (standard) standard.checked = false;
      } else if (input.matches('[data-fe-no-packaging]') && input.checked) {
        for (const box of boxes) if (box.checked) { box.checked = false; box.dispatchEvent(new Event('change', { bubbles: true })); }
      }
      if (standard) standard.checked = !boxes.some(box => box.checked);
      this.schedule();
    }
    readAmount(text) {
      const clean = (text || '').replace(/[^0-9,.-]/g, '');
      const normalized = this.decimal === ',' ? clean.replace(/\./g, '').replace(',', '.') : clean.replace(/,/g, '');
      const amount = Number(normalized);
      return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
    }
    setText(node, value) { if (node && node.textContent !== value) node.textContent = value; }
    addRow(fragment, label, value) {
      const row = document.createElement('div');
      const dt = document.createElement('dt');
      const dd = document.createElement('dd');
      dt.textContent = label; dd.textContent = value; row.append(dt, dd); fragment.append(row);
    }
    photoState(input) {
      const field = input.closest('.gpo-element');
      if (field?.querySelector('.gpo-uploading')) return 'pending';
      if (field?.classList.contains('has-error') || field?.querySelector('.dz-error')) return 'error';
      if (field?.classList.contains('has-success')) return 'ready';
      if (input.files?.length) return 'pending';
      return 'missing';
    }
    render() {
      this.observer.disconnect();
      try {
        const message = this.config.messages;
        const form = this.root.querySelector('form[data-type="add-to-cart-form"]');
        const variantId = form?.querySelector('[name="id"]')?.value;
        const variant = this.config.variants.find(v => String(v.id) === variantId) || this.config.variants[0];
        const quantity = Math.max(1, Number(form?.querySelector('[name="quantity"]')?.value) || 1);
        const files = [...this.root.querySelectorAll('.gpo-app input[type="file"]')];
        const rows = document.createDocumentFragment();
        const variantTitle = variant?.title || '';
        if (variantTitle && variantTitle !== 'Default Title') this.addRow(rows, this.config.flow === 'eye_pair' ? message.color : message.style, variantTitle);
        if (quantity > 1) this.addRow(rows, message.quantity, String(quantity));
        const app = this.root.querySelector('.gpo-app');
        const firstOptional = app?.querySelector('.gpo-element:not(:has(input[type="file"]))');
        if (this.config.flow.startsWith('eye_') && firstOptional && !app.querySelector('.fe-optional-heading')) {
          const heading = document.createElement('p'); heading.className = 'fe-optional-heading'; heading.textContent = message.optional;
          firstOptional.before(heading);
        }
        const photos = document.createDocumentFragment();
        for (const file of files) {
          const field = file.closest('.gpo-element');
          const currentLabel = field?.querySelector('.label-content')?.textContent?.trim() || file.dataset.fieldName || file.name;
          const isWoman = /damen/i.test(file.dataset.fieldName || file.name);
          const label = this.config.flow === 'eye_pair' ? (isWoman ? message.woman : message.man) : (currentLabel || message.singlePhoto);
          if (this.config.flow === 'eye_pair') this.setText(field?.querySelector('.label-content'), label);
          if (field && !field.querySelector('.fe-upload-description')) {
            const hint = document.createElement('span'); hint.className = 'fe-upload-description'; hint.textContent = message.uploadHint;
            hint.id = `FeHint-${this.dataset.section}-${file.id}`;
            field.querySelector('.gpo-label')?.after(hint);
            file.setAttribute('aria-describedby', [file.getAttribute('aria-describedby'), hint.id].filter(Boolean).join(' '));
          }
          const state = this.photoState(file);
          this.addRow(rows, label, state === 'ready' ? message.uploaded : state === 'pending' ? message.uploading : state === 'error' ? message.failed : message.missing);
          const image = file.files?.[0];
          let preview = this.previews.get(file);
          if (preview && preview.file !== image) { URL.revokeObjectURL(preview.url); this.previews.delete(file); preview = null; }
          if (image && image.type.startsWith('image/') && !preview) { preview = { file: image, url: URL.createObjectURL(image) }; this.previews.set(file, preview); }
          const remotePhoto = state === 'ready' ? field?.querySelector('.gpo-f-card .gpo-f-link img')?.getAttribute('src') : null;
          if (preview || remotePhoto) {
            const card = document.createElement('div'); card.className = 'fe-summary-photo';
            const img = document.createElement('img'); img.src = preview?.url || remotePhoto; img.alt = label; img.width = 44; img.height = 44;
            const caption = document.createElement('span'); caption.textContent = label; card.append(img, caption); photos.append(card);
          }
        }
        const engravings = [];
        for (const input of this.root.querySelectorAll('.gpo-app input:not([type="file"]):not([type="hidden"]),.gpo-app textarea,.gpo-app select')) {
          if (input.disabled || !input.value || !input.closest('.gpo-element')?.getClientRects().length) continue;
          if (['checkbox', 'radio'].includes(input.type) && !input.checked) continue;
          const label = input.closest('.gpo-element').querySelector('.label-content')?.textContent?.trim() || input.dataset.fieldName || input.name;
          if (input.dataset.type === 'google-font') {
            if ([...this.root.querySelectorAll('.gpo-app input[type="checkbox"]')].some(i => i.checked)) this.addRow(rows, message.font, input.value);
          } else engravings.push(`${label.replace(/\?$/, '')}: ${input.value}`);
        }
        if (engravings.length) this.addRow(rows, message.engraving, engravings.join(' · '));
        const extras = [...this.root.querySelectorAll('.fe-upsell input[name="bundles"]:checked:not(:disabled)')];
        this.addRow(rows, message.packaging, extras.length ? extras.map(input => input.closest('.fe-upsell__card')?.querySelector('.fe-upsell__product-title')?.textContent.trim()).filter(Boolean).join(' · ') : message.none);
        const extraPriceNode = app?.querySelector('.gpo-total-text .money');
        const extraPrice = extraPriceNode && extraPriceNode.closest('.gpo-total-text').getClientRects().length ? this.readAmount(extraPriceNode.textContent) : 0;
        const bundlesPrice = extras.reduce((sum, input) => sum + (Number(input.dataset.price) || 0), 0);
        const total = ((variant?.price || 0) + extraPrice) * quantity + bundlesPrice;
        this.setText(this.querySelector('[data-fe-total]'), this.money.format(total / 100));
        this.querySelector('[data-fe-selections]').replaceChildren(rows);
        this.querySelector('[data-fe-photo-previews]').replaceChildren(photos);
        const states = files.map(file => this.photoState(file));
        const expected = Number(this.config.expectedFiles || 0);
        const state = expected === 0 ? 'ready' : files.length < expected ? 'missing' : states.includes('error') ? 'error' : states.includes('pending') ? 'pending' : states.slice(0, expected).every(s => s === 'ready') ? 'ready' : 'missing';
        const status = this.querySelector('[data-fe-upload-status]');
        status.hidden = expected === 0;
        status.dataset.state = state;
        const missingText = expected === 1 ? message.missingSingle : message.missing;
        this.setText(status, state === 'ready' ? message.uploaded : state === 'pending' ? message.uploading : state === 'error' ? message.failed : missingText);
        this.root.dispatchEvent(new CustomEvent('fe:summary-updated', { bubbles: true, detail: { total: this.money.format(total / 100), state } }));
      } finally { this.observe(); }
    }
    syncReviews() {
      const proof = this.root.querySelector('[data-fe-customer-proof]');
      if (!proof) return;
      const urls = [...this.reviewWidget.querySelectorAll('.jdgm-gallery__thumbnail')].map(img => img.getAttribute('data-src') || img.getAttribute('src')).filter(Boolean).slice(0, 3);
      const signature = urls.join('|');
      if (!urls.length || signature === this.reviewSignature) return;
      this.reviewSignature = signature;
      const fragment = document.createDocumentFragment();
      for (const url of urls) {
        const a = document.createElement('a'); a.href = '#judgeme_product_reviews';
        const img = document.createElement('img'); img.src = url; img.alt = this.config.messages.customerPhoto; img.loading = 'lazy'; img.width = 160; img.height = 160;
        a.append(img); fragment.append(a);
      }
      proof.querySelector('[data-fe-review-photos]').replaceChildren(fragment); proof.hidden = false;
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null; this.observer?.disconnect(); this.reviewObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.renderTimer) clearTimeout(this.renderTimer);
      for (const preview of this.previews?.values() || []) URL.revokeObjectURL(preview.url);
    }
  }
  customElements.define('fe-order-summary', FeOrderSummary);
})();
